import React, { useState } from "react";
import { useTools } from "../hooks/useTools";
import ToolCard from "../components/ToolCard";

const categories = [
  { label: "All", value: undefined, icon: "🔍" },
  { label: "Finance", value: "finance", icon: "💰" },
  { label: "Analytics", value: "analytics", icon: "📊" },
  { label: "Marketing", value: "marketing", icon: "📣" },
  { label: "Design", value: "design", icon: "🎨" },
  { label: "Developer", value: "developer", icon: "⚡" },
  { label: "Productivity", value: "productivity", icon: "🚀" },
];

export default function Tools() {
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState("");
  const { tools: allTools, loading, error } = useTools(selected);

  const tools = search.trim()
    ? allTools.filter(
        (t) =>
          t.name?.toLowerCase().includes(search.toLowerCase()) ||
          t.description?.toLowerCase().includes(search.toLowerCase())
      )
    : allTools;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm mb-2">Tools</p>
          <h1 className="text-4xl font-extrabold text-gray-900 mb-2">All Tools</h1>
          <p className="text-gray-500 text-lg">Browse and run professional tools — no setup required.</p>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        {/* Search + category filter row */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1 max-w-sm">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tools…"
              className="w-full pl-9 pr-4 py-2 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 placeholder-gray-400"
            />
          </div>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((cat) => {
            const active = selected === cat.value;
            return (
              <button
                key={cat.label}
                onClick={() => setSelected(cat.value)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                  active
                    ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md"
                    : "bg-white border border-gray-200 text-gray-600 hover:border-indigo-400 hover:text-indigo-600 hover:shadow-sm"
                }`}
              >
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map((i) => (
              <div key={i} className="border border-gray-200 rounded-2xl p-6 animate-pulse bg-white">
                <div className="h-4 bg-gray-200 rounded w-20 mb-3" />
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-full" />
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-5 py-4 text-sm">
            Error loading tools: {error}
          </div>
        )}

        {!loading && tools.length === 0 && (
          <div className="text-center py-20">
            <p className="text-5xl mb-4">🔍</p>
            <p className="text-gray-500 text-lg">No tools found in this category.</p>
          </div>
        )}

        {!loading && tools.length > 0 && (
          <>
            <p className="text-sm text-gray-400 mb-5">{tools.length} tool{tools.length !== 1 ? "s" : ""} found</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tools.map((t) => <ToolCard key={t.slug} tool={t} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
